/*
 * User-triggered prettify transform for munging-expression editors (templated TS with `{{...}}`
 * tokens). Never run on load/save; tokens are masked so a plain-TS formatter can parse the rest.
 */

const MASK_TOKEN = /\{\{[\s\S]*?\}\}/g
// Template literals are masked (and restored verbatim) so prettier never reindents their interior
// and the function-body dedent below cannot strip leading spaces from their inner lines.
const TEMPLATE_LITERAL = /`(?:\\[\s\S]|[^`\\])*`/g

/** Replace each match with a placeholder identifier so a plain-TS formatter can parse the rest. */
function mask(src: string, pattern: RegExp, prefix: string, store: string[]): string {
  return src.replace(pattern, (match) => {
    const id = `__${prefix}_${store.length}__`
    store.push(match)
    return id
  })
}

function unmask(src: string, prefix: string, store: string[]): string {
  return src.replace(new RegExp(`__${prefix}_(\\d+)__`, 'g'), (match, n) => store[Number(n)] ?? match)
}

/**
 * Pretty-print a munging expression. Wraps the body in a function so a top-level `return` parses.
 * Throws on unparseable input — callers should leave the text unchanged.
 */
export async function formatExpression(src: string): Promise<string> {
  if (src.trim() === '') {
    return src
  }
  // Mask templates before tokens so a `{{...}}` inside a template literal stays inside it.
  const templates: string[] = []
  const tokens: string[] = []
  const masked = mask(mask(src, TEMPLATE_LITERAL, 'MUNGE_TPL', templates), MASK_TOKEN, 'MUNGE', tokens)
  const [prettier, estree, babel] = await Promise.all([
    import('prettier/standalone'),
    import('prettier/plugins/estree'),
    import('prettier/plugins/babel'),
  ])
  const wrapped = `function __munge__() {\n${masked}\n}`
  const formatted = await prettier.format(wrapped, {
    parser: 'babel',
    plugins: [(estree as any).default ?? estree, (babel as any).default ?? babel],
    singleQuote: true,
    semi: true,
    printWidth: 80,
  })
  const lines = formatted.replace(/\n$/, '').split('\n')
  const body = lines
    .slice(1, -1)
    .map((line) => (line.startsWith('  ') ? line.slice(2) : line))
    .join('\n')
  return unmask(unmask(body, 'MUNGE', tokens), 'MUNGE_TPL', templates)
}
